import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { STATUS_META, statusLabel, formatSqYd } from "@/lib/statusUtils";
import PlotDetailDialog from "@/components/PlotDetailDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Download, X, SlidersHorizontal } from "lucide-react";

// Common quick-select plot sizes (sq. yards).
const QUICK_SIZES = [120, 198, 204, 208.8, 240, 258, 266.6, 276, 282, 300, 306, 324, 330, 354, 360, 366, 384, 408, 480, 506];

const PHASE_LP_MAP = {
  1: "256/2022/H",
  2: "06/2024/H",
  3: "08/2024/H",
  4: "18/2024/H",
  5: "41/2024/H",
  6: "68/2024/H",
  7: "69/2024/H",
  8: "TBD",
};

const emptyFilters = {
  plot_no: "",
  phase_no: "all",
  lp_no: "all",
  exact_size: "",
  min_size: "",
  max_size: "",
  status: "all",
  vacant_portion: "all",
  payment: "all",   // all / emp_eligible / emp_not_eligible
  mortgage: "all",  // all / yes / no
};

export default function Inventory() {
  const { user } = useAuth();
  const [plots, setPlots] = useState([]);
  const [lps, setLps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const buildParams = (f) => {
    const p = {};
    if (f.plot_no) p.plot_no = Number(f.plot_no);
    if (f.phase_no && f.phase_no !== "all") p.phase_no = Number(f.phase_no);
    if (f.lp_no && f.lp_no !== "all") p.lp_no = f.lp_no;
    if (f.exact_size) p.exact_size = Number(f.exact_size);
    if (f.min_size) p.min_size = Number(f.min_size);
    if (f.max_size) p.max_size = Number(f.max_size);
    if (f.status && f.status !== "all") p.status_filter = f.status;
    if (f.vacant_portion && f.vacant_portion !== "all") p.vacant_portion = f.vacant_portion;
    if (f.payment === "emp_eligible") p.payment_plan_allowed = true;
    if (f.payment === "emp_not_eligible") p.payment_plan_allowed = false;
    if (f.mortgage === "yes") p.mortgage_flag = true;
    if (f.mortgage === "no") p.mortgage_flag = false;
    p.limit = 2000;
    return p;
  };

  const fetchPlots = async (f) => {
    setLoading(true);
    try {
      const [pRes, lRes] = await Promise.all([
        api.get("/plots", { params: buildParams(f) }),
        lps.length ? Promise.resolve({ data: lps }) : api.get("/lps"),
      ]);
      setPlots(pRes.data);
      if (!lps.length) setLps(lRes.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { fetchPlots(emptyFilters); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => { setApplied(filters); fetchPlots(filters); };
  const clearFilters = () => { setFilters(emptyFilters); setApplied(emptyFilters); fetchPlots(emptyFilters); };
  const applySizeChip = (size) => {
    const next = { ...filters, exact_size: String(size), min_size: "", max_size: "" };
    setFilters(next); setApplied(next); fetchPlots(next);
  };

  const activeChipSize = Number(applied.exact_size) || null;
  const filteredCount = plots.length;

  const openPlot = async (p) => {
    try { const { data } = await api.get(`/plots/${p.id}`); setSelectedPlot(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportUrl = useMemo(() => {
    const q = new URLSearchParams();
    Object.entries(buildParams(applied)).forEach(([k, v]) => q.append(k, String(v)));
    q.delete("limit");
    return `${API_BASE}/export/plots.xlsx?${q.toString()}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const doExport = () => {
    const token = localStorage.getItem("anu_token");
    fetch(exportUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "anuttara-plots.xlsx"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C06C47] font-semibold mb-2">Inventory Explorer</div>
            <h1 className="font-heading text-2xl md:text-4xl">Plot Inventory</h1>
            <p className="text-sm text-[#5C6653] mt-2">Advanced search across all phases, sizes, and payment eligibility.</p>
          </div>
          {isAdmin && (
            <Button data-testid="export-plots-btn" variant="outline" className="rounded-sm w-fit" onClick={doExport}>
              <Download className="h-4 w-4 mr-2" /> Export Excel
            </Button>
          )}
        </div>

        {/* Advanced Search */}
        <div className="border border-[#E5E2DC] bg-white p-4 md:p-5 mb-4" data-testid="advanced-search-panel">
          <div className="flex items-center gap-2 mb-3">
            <SlidersHorizontal className="h-4 w-4 text-[#234B36]" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#234B36]">Advanced Search</div>
          </div>

          {/* Quick size chips */}
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="size-chips">
            {QUICK_SIZES.map((s) => {
              const active = activeChipSize === s;
              return (
                <button
                  key={s}
                  onClick={() => applySizeChip(s)}
                  data-testid={`size-chip-${s}`}
                  className={`text-xs px-2.5 py-1 border rounded-full transition-colors ${
                    active ? "bg-[#234B36] text-white border-[#234B36]" : "bg-white text-[#1A1F16] border-[#E5E2DC] hover:bg-[#F0EFEA]"
                  }`}
                >
                  {s} sq.yd
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Field label="Plot number">
              <Input value={filters.plot_no} onChange={(e) => setFilters({ ...filters, plot_no: e.target.value })} placeholder="e.g. 42" data-testid="filter-plot-no" className="rounded-sm h-9" />
            </Field>
            <Field label="Phase">
              <Select value={filters.phase_no} onValueChange={(v) => setFilters({ ...filters, phase_no: v })}>
                <SelectTrigger data-testid="filter-phase" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All phases</SelectItem>
                  {Object.entries(PHASE_LP_MAP).map(([ph, lp]) => (
                    <SelectItem key={ph} value={ph}>Phase {ph} — LP {lp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="LP number">
              <Select value={filters.lp_no} onValueChange={(v) => setFilters({ ...filters, lp_no: v })}>
                <SelectTrigger data-testid="filter-lp" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All LPs</SelectItem>
                  {Object.values(PHASE_LP_MAP).filter((lp) => lp !== "TBD").map((lp) => (
                    <SelectItem key={lp} value={lp}>{lp}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Exact size (sq.yd)">
              <Input value={filters.exact_size} onChange={(e) => setFilters({ ...filters, exact_size: e.target.value, min_size: "", max_size: "" })} placeholder="e.g. 240" data-testid="filter-exact-size" className="rounded-sm h-9" />
            </Field>
            <Field label="Min size">
              <Input value={filters.min_size} onChange={(e) => setFilters({ ...filters, min_size: e.target.value, exact_size: "" })} placeholder="e.g. 200" data-testid="filter-min-size" className="rounded-sm h-9" />
            </Field>
            <Field label="Max size">
              <Input value={filters.max_size} onChange={(e) => setFilters({ ...filters, max_size: e.target.value, exact_size: "" })} placeholder="e.g. 300" data-testid="filter-max-size" className="rounded-sm h-9" />
            </Field>
            <Field label="Status">
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="filter-status" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="fully_vacant">Fully Available</SelectItem>
                  <SelectItem value="partially_vacant">Partially Available</SelectItem>
                  <SelectItem value="booked">Booked / Reserved</SelectItem>
                  <SelectItem value="sold">Sold / Registered</SelectItem>
                  <SelectItem value="hold">Hold</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="mortgage">Mortgage</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Vacant portion">
              <Select value={filters.vacant_portion} onValueChange={(v) => setFilters({ ...filters, vacant_portion: v })}>
                <SelectTrigger data-testid="filter-portion" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any portion</SelectItem>
                  <SelectItem value="F">Full</SelectItem>
                  <SelectItem value="N">North</SelectItem>
                  <SelectItem value="S">South</SelectItem>
                  <SelectItem value="E">East</SelectItem>
                  <SelectItem value="W">West</SelectItem>
                  <SelectItem value="P">Partial</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Payment plan">
              <Select value={filters.payment} onValueChange={(v) => setFilters({ ...filters, payment: v })}>
                <SelectTrigger data-testid="filter-payment" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="emp_eligible">EMP eligible (Phase 4 / 5)</SelectItem>
                  <SelectItem value="emp_not_eligible">Full payment only</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Mortgage">
              <Select value={filters.mortgage} onValueChange={(v) => setFilters({ ...filters, mortgage: v })}>
                <SelectTrigger data-testid="filter-mortgage" className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  <SelectItem value="yes">Flagged</SelectItem>
                  <SelectItem value="no">Not flagged</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button onClick={applyFilters} data-testid="apply-filters-btn" className="rounded-sm bg-[#234B36] hover:bg-[#1A3A29] text-white h-9">
              <Search className="h-4 w-4 mr-2" /> Apply
            </Button>
            <Button onClick={clearFilters} data-testid="clear-filters-btn" variant="outline" className="rounded-sm h-9">
              <X className="h-4 w-4 mr-2" /> Clear
            </Button>
            <div className="ml-auto text-sm text-[#5C6653]">
              <span className="font-mono-anu text-[#1A1F16]">{filteredCount}</span> plot(s)
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="border border-[#E5E2DC] bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="inventory-table">
            <thead className="bg-[#F0EFEA] text-left">
              <tr className="border-b border-[#E5E2DC]">
                <Th>Plot #</Th>
                <Th>Phase / LP</Th>
                <Th>Total (sq.yd)</Th>
                <Th>Portions</Th>
                <Th>Vacant (sq.yd)</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th>Mortgage</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#5C6653]">Loading…</td></tr>
              ) : plots.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#5C6653]">No plots match your filters.</td></tr>
              ) : (
                plots.slice(0, 500).map((p) => {
                  const meta = STATUS_META[p.status] || STATUS_META.unknown;
                  return (
                    <tr
                      key={p.id}
                      onClick={() => openPlot(p)}
                      data-testid={`inventory-row-${p.plot_no}-${(p.lp_no || "").replace(/\//g, "-")}`}
                      className="border-b border-[#E5E2DC] hover:bg-[#F9F8F6] cursor-pointer"
                    >
                      <Td><span className="font-mono-anu font-medium">#{p.plot_no}</span></Td>
                      <Td><span className="text-xs text-[#5C6653]">{p.phase_name}</span> · <span className="font-medium">{p.lp_no}</span></Td>
                      <Td>{formatSqYd(p.total_sqyd)}</Td>
                      <Td>
                        {p.is_split ? (
                          <span className="text-xs px-2 py-0.5 border font-medium inline-flex items-center gap-1.5" style={{ background: "#F4E6D9", borderColor: "#C06C47", color: "#8B4A2B" }} data-testid={`portion-badge-${p.plot_no}`}>
                            {p.portion_count} · {(p.portions || []).map((x) => x.direction).sort((a, b) => "NSEW".indexOf(a) - "NSEW".indexOf(b)).join("/")}
                            {p.has_pending_portion && (
                              <span
                                className="inline-block h-1.5 w-1.5 rounded-full bg-[#EAB308] ring-1 ring-white"
                                title="Awaiting Super Admin approval"
                                data-testid={`portion-pending-dot-${p.plot_no}`}
                                aria-label="Pending Super Admin approval"
                              />
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-[#5C6653]">Full</span>
                        )}
                      </Td>
                      <Td>{p.vacant_sqyd ? formatSqYd(p.vacant_sqyd) : "—"}</Td>
                      <Td>
                        <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: `${meta.bg}20`, borderColor: meta.color, color: meta.color }}>
                          {statusLabel(p.status)}
                        </span>
                      </Td>
                      <Td>
                        {p.payment_plan_allowed ? (
                          <span className="text-xs px-2 py-0.5 border font-medium" style={{ background: "#34735515", borderColor: "#347355", color: "#347355" }}>EMP</span>
                        ) : (
                          <span className="text-xs text-[#5C6653]">Full only</span>
                        )}
                      </Td>
                      <Td>{p.mortgage_flag ? <span className="text-[#C06C47] text-xs font-medium">Flagged</span> : <span className="text-xs text-[#5C6653]">—</span>}</Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {plots.length > 500 && (
            <div className="text-xs text-[#5C6653] text-center py-3 border-t border-[#E5E2DC]">Showing first 500 rows — refine filters to narrow.</div>
          )}
        </div>
      </div>

      <PlotDetailDialog
        plot={selectedPlot}
        onClose={() => setSelectedPlot(null)}
        onBooked={() => { setSelectedPlot(null); fetchPlots(applied); }}
      />
    </AppShell>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-2.5">{children}</td>; }
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653] font-semibold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
